'use client';

import { useCallback, useEffect, useState } from 'react';
import * as Blockly from 'blockly/core';
import { Plus, Variable as VariableIcon, X } from 'lucide-react';
import { useBlockModalStore } from '@/stores/useBlockModalStore';
import { useBlockModalVariableStore } from '@/stores/useBlockModalVariableStore';
import IconAdaptive from '@/components/IconAdaptive';

type VarType = 'float' | 'String' | 'bool';

interface VarModel {
    getId(): string;
    getName(): string;
    getType(): string;
}

const PRESET_NAMES = ['i', 'lat', 'lng', 'str', 'flag'] as const;
const VAR_TYPE_CONFIG: Record<VarType, { label: string; color: string; order: number }> = {
    float: { label: '数値', color: '#FF1BAC', order: 0 },
    String: { label: '文字列', color: '#FF4081', order: 1 },
    bool: { label: '真偽', color: '#da00bd', order: 2 },
};

const isValidIdentifier = (value: string) => /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(value);

function getVarMap(workspace: Blockly.WorkspaceSvg) {
    return workspace.getVariableMap();
}

function getAllVars(workspace: Blockly.WorkspaceSvg): VarModel[] {
    return getVarMap(workspace).getAllVariables() as unknown as VarModel[];
}

function getSortedVars(workspace: Blockly.WorkspaceSvg): VarModel[] {
    return [...getAllVars(workspace)].sort((a, b) => {
        const aOrder = VAR_TYPE_CONFIG[a.getType() as VarType]?.order ?? 999;
        const bOrder = VAR_TYPE_CONFIG[b.getType() as VarType]?.order ?? 999;
        return aOrder - bOrder;
    });
}

export default function Variable() {
    const { closeModal, setOnConfirm } = useBlockModalStore();
    const { workspace, fieldRef, filterType } = useBlockModalVariableStore();

    const [, forceUpdate] = useState(0);
    const [newName, setNewName] = useState('');
    const [newType, setNewType] = useState<VarType>(filterType ?? 'float');
    const [addError, setAddError] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(() => {
        if (!workspace || !fieldRef) return null;
        const currentName = fieldRef.getValue();
        const match = getAllVars(workspace).find((v) => v.getName() === currentName);
        return match?.getId() ?? null;
    });

    useEffect(() => {
        if (filterType) setNewType(filterType);
    }, [filterType]);

    const vars = workspace ? getSortedVars(workspace) : [];

    const handleAdd = () => {
        const trimmed = newName.trim();
        if (!trimmed) {
            setAddError('変数名を入力してください');
            return;
        }
        if (!isValidIdentifier(trimmed)) {
            setAddError('変数名は英字またはアンダースコアで始まる半角英数字のみ使用できます');
            return;
        }
        if (PRESET_NAMES.includes(trimmed as (typeof PRESET_NAMES)[number])) {
            setAddError('この名前は予約済みです');
            return;
        }
        if (!workspace) return;
        const existing = getAllVars(workspace).find((v) => v.getName() === trimmed);
        if (existing) {
            setAddError('同じ名前の変数が既に存在します');
            return;
        }

        const created = getVarMap(workspace).createVariable(trimmed, newType);
        const createdId = created?.getId() ?? null;
        if (createdId && (!filterType || filterType === newType)) {
            setSelectedId(createdId);
        }
        setNewName('');
        setAddError('');
        forceUpdate((n) => n + 1);
    };

    const handleDelete = (id: string) => {
        if (!workspace) return;
        const varMap = getVarMap(workspace);
        const v = varMap.getVariableById(id);
        if (!v) return;
        varMap.deleteVariable(v);
        if (selectedId === id) setSelectedId(null);
        forceUpdate((n) => n + 1);
    };

    const handleOk = useCallback(() => {
        if (!workspace) {
            closeModal();
            return;
        }
        if (fieldRef && selectedId) {
            const v = getVarMap(workspace).getVariableById(selectedId);
            if (v) fieldRef.setVarName(v.getName());
        }
        closeModal();
    }, [closeModal, workspace, fieldRef, selectedId]);

    useEffect(() => {
        setOnConfirm(handleOk);
    }, [handleOk, setOnConfirm]);

    return (
        <div className="flex flex-col h-full bg-color-block-variable-modal-bg rounded-2xl overflow-hidden">
            {/* ヘッダー */}
            <div className="flex items-center px-3 py-1 bg-color-block-variable-modal-header flex-shrink-0">
                <span className="text-xl mr-2">
                    <IconAdaptive Icon={VariableIcon} strokeWidth={2} baseSize={40} className="text-white" />
                </span>
                <span className="text-color-white-base font-semibold text-sm">変数の管理</span>
            </div>

            {/* 登録済み変数リスト */}
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2 min-h-0">
                <p className="text-color-block-action-modal-text-muted text-xs mb-2">変数 選択</p>
                {vars.length === 0 && <p className="text-white/40 text-xs text-center py-4">変数がありません</p>}
                {vars.map((v) => {
                    const isPreset = PRESET_NAMES.includes(v.getName() as (typeof PRESET_NAMES)[number]);
                    const selectable = !filterType || filterType === v.getType();
                    const isSelected = selectedId === v.getId();
                    const typeConfig = VAR_TYPE_CONFIG[v.getType() as VarType];
                    return (
                        <div
                            key={v.getId()}
                            onClick={() => selectable && setSelectedId(v.getId())}
                            className={`flex items-center gap-2 rounded-lg overflow-hidden flex-shrink-0 transition-colors ${
                                selectable ? 'cursor-pointer' : 'cursor-not-allowed'
                            }`}
                        >
                            <span
                                className={`w-14 text-center py-2 text-white text-xs font-medium flex-shrink-0 self-stretch flex items-center justify-center`}
                                style={{ backgroundColor: typeConfig?.color ?? '#888' }}
                            >
                                {typeConfig?.label ?? v.getType()}
                            </span>
                            <span
                                className={`flex-1 text-color-white-base text-sm px-2 py-2 transition-colors ${
                                    isSelected
                                        ? 'bg-color-block-variable-modal-header ring-1 ring-white/60'
                                        : selectable
                                        ? 'bg-color-block-variable-modal-row-bg hover:bg-color-block-variable-modal-button-bg'
                                        : 'bg-color-block-variable-modal-row-bg opacity-35'
                                }`}
                            >
                                {v.getName()}
                            </span>
                            <span className="bg-color-block-variable-modal-row-bg self-stretch flex items-center px-2 w-8 justify-center">
                                {!isPreset && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(v.getId());
                                        }}
                                        className="text-white/40 hover:text-white/80 transition-colors"
                                    >
                                        <IconAdaptive Icon={X} strokeWidth={2} baseSize={16} />
                                    </button>
                                )}
                            </span>
                        </div>
                    );
                })}
            </div>

            {/* フッター */}
            <div className="flex justify-between items-start gap-2 px-5 py-3 border-t border-color-block-variable-modal-modal-border flex-shrink-0">
                <div className="flex flex-col gap-1">
                    <div className="flex gap-2 items-center">
                        <input
                            className="bg-color-block-variable-modal-button-bg text-color-white-base border border-white/20 rounded px-3 py-1.5 text-sm outline-none focus:border-[#FF1BAC] placeholder:text-white/30 w-28"
                            placeholder="変数名"
                            value={newName}
                            inputMode="url"
                            onChange={(e) => {
                                setNewName(e.target.value);
                                setAddError('');
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleAdd();
                            }}
                        />
                        <select
                            className="bg-color-block-variable-modal-button-bg text-color-white-base border border-white/20 rounded px-2 py-1.5 text-sm outline-none flex-shrink-0"
                            value={newType}
                            onChange={(e) => setNewType(e.target.value as VarType)}
                        >
                            <option value="float">数値</option>
                            <option value="String">文字列</option>
                            <option value="bool">真偽</option>
                        </select>
                        <button
                            onClick={handleAdd}
                            className={`flex items-center pl-4 pr-5 py-1.5 bg-color-block-variable-modal-button-bg border border-dashed border-white/30 rounded-lg text-white/50 text-xs hover:border-color-block-variable-modal-header hover:text-color-block-variable-modal-header transition-colors flex-shrink-0 ${
                                newName.length > 0 ? 'animate-pulse' : ''
                            }`}
                            style={
                                newName.length > 0
                                    ? {
                                          borderColor: VAR_TYPE_CONFIG[newType].color,
                                          color: VAR_TYPE_CONFIG[newType].color,
                                      }
                                    : {}
                            }
                        >
                            <IconAdaptive Icon={Plus} strokeWidth={2} baseSize={16} className="mr-1" />
                            変数を追加
                        </button>
                    </div>
                    {addError && <p className="text-red-400 text-xs">{addError}</p>}
                </div>

                <div className="flex gap-2 flex-shrink-0">
                    <button
                        onClick={closeModal}
                        className="px-4 py-2 bg-color-block-variable-modal-cancel text-white/70 rounded-lg text-sm"
                    >
                        キャンセル
                    </button>
                    <button
                        onClick={handleOk}
                        className="px-6 py-2 bg-color-block-variable-modal-ok text-color-white-base rounded-lg text-sm font-semibold hover:bg-color-block-variable-modal-ok-hover transition-colors"
                    >
                        OK
                    </button>
                </div>
            </div>
        </div>
    );
}
